import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import ComplaintForm from './components/ComplaintForm';
import SubmissionGuidelines from './components/SubmissionGuidelines';
import SuccessModal from './components/SuccessModal';
import ProgressIndicator from './components/ProgressIndicator';
import Icon from '../../components/AppIcon';

const ComplaintSubmission = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [completedFields, setCompletedFields] = useState(0);

  // Check localStorage for language preference
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language') || 'th';
    // In a real app, this would update the language context
    console.log('Current language:', savedLanguage);
  }, []);

  const generateReferenceNumber = () => {
    const date = new Date();
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    const day = String(date?.getDate())?.padStart(2, '0');
    const random = Math.floor(Math.random() * 9999)?.toString()?.padStart(4, '0');
    return `CMP${year}${month}${day}${random}`;
  };

  const handleFormSubmit = async (result) => {
    setIsSubmitting(true);
    
    try {
      if (result.success) {
        // Set the ticket number from Supabase
        setReferenceNumber(result.ticketNo);
      setShowSuccessModal(true);
        console.log('Complaint submitted successfully:', result);
      } else {
        // Handle error
        console.error('Submission failed:', result.error);
        // You could show an error toast here
        alert('เกิดข้อผิดพลาด: ' + result.error);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      alert('เกิดข้อผิดพลาดที่ไม่คาดคิด');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/public-services-portal');
  };

  // Calculate form completion progress
  const calculateProgress = (formData) => {
    const requiredFields = ['fullName', 'contactNumber', 'category', 'description', 'privacyConsent'];
    const completed = requiredFields?.filter(field => {
      if (field === 'privacyConsent') {
        return formData && formData?.[field] === true;
      }
      return formData && formData?.[field] && formData?.[field]?.trim();
    })?.length;
    
    setCompletedFields(completed);
    
    // Update current step based on completion
    if (completed === 0) setCurrentStep(0);
    else if (completed <= 2) setCurrentStep(0);
    else if (completed <= 4) setCurrentStep(1);
    else setCurrentStep(2);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <BreadcrumbTrail />
        
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="MessageSquare" size={24} color="white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                ยื่นเรื่องร้องเรียน
              </h1>
              <p className="text-muted-foreground mt-1">
                ระบบรับเรื่องร้องเรียนออนไลน์ อำเภอเทิง
              </p>
            </div>
          </div>
          
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-accent-foreground">
                  <strong>หมายเหตุ:</strong> กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง 
                  เพื่อให้เจ้าหน้าที่สามารถดำเนินการและติดต่อกลับได้อย่างรวดเร็ว
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            <ComplaintForm 
              onSubmit={handleFormSubmit}
              isSubmitting={isSubmitting}
            />
          </div>

          {/* Right Column - Guidelines and Progress */}
          <div className="lg:col-span-1 space-y-6">
            <ProgressIndicator
              currentStep={currentStep}
              totalSteps={3}
              completedFields={completedFields}
              totalFields={5}
            />
            
            <SubmissionGuidelines />
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-12 bg-muted/30 rounded-lg p-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <img 
                src="/assets/images/b822850b984eba9df2620697399b50075bab49cf64ce35210033003315257900-1757413105446.png" 
                alt="ตราสิงที่ทำการปกครองอำเภอเทิง" 
                className="w-12 h-12 object-contain"
              />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              ความปลอดภัยของข้อมูล
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              ข้อมูลส่วนบุคคลของท่านจะถูกเก็บรักษาอย่างปลอดภัยตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 
              และจะใช้เพื่อการดำเนินการตามเรื่องร้องเรียนเท่านั้น เจ้าหน้าที่จะติดต่อกลับเฉพาะในกรณีที่จำเป็นเท่านั้น
            </p>
          </div>
        </div>
      </main>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        referenceNumber={referenceNumber}
      />
    </div>
  );
};

export default ComplaintSubmission;